import { supabase } from '../supabaseClient';

export class TaskNotificationService {
  static baseUrl = '/.netlify/functions';

  // Helper method to convert priority number to text
  static getPriorityText(priority) {
    switch (priority) {
      case 1: return 'high';
      case 2: return 'medium';
      case 3: return 'low';
      default: return 'normal';
    }
  }

  // Enhanced method to send in-app notifications only
  static async sendNotification(type, data) {
    try {
      // Create in-app notification only
      await this.createInAppNotification(type, data);
      console.log('In-app notification sent successfully');
    } catch (error) {
      console.error('Error sending notification:', error);
      throw error;
    }
  }

  // Create in-app notification
  static async createInAppNotification(type, data) {
    try {
      let title, message, actionUrl;

      switch (type) {
        case 'task_assigned':
          title = `New Task Assigned: ${data.task.title}`;
          message = `You have been assigned a new ${this.getPriorityText(data.task.priority)} priority task.`;
          actionUrl = `/status-board?task=${data.task.id}`;
          break;

        case 'task_reassigned':
          title = `Task Reassigned: ${data.task.title}`;
          message = `A task has been reassigned to you from ${data.previousAssignee?.name || 'another user'}.`;
          actionUrl = `/status-board?task=${data.task.id}`;
          break;

        case 'task_overdue':
          title = `Overdue Task: ${data.task.title}`;
          message = `This task was due on ${new Date(data.task.due_date).toLocaleDateString()}.`;
          actionUrl = `/status-board?task=${data.task.id}`;
          break;

        case 'task_updated':
          title = `Task Updated: ${data.task.title}`;
          message = `Task details have been updated.`;
          actionUrl = `/status-board?task=${data.task.id}`;
          break;

        default:
          title = `Task Notification: ${data.task.title}`;
          message = 'You have a task notification.';
          actionUrl = `/status-board?task=${data.task.id}`;
      }

      // Ensure we have valid UUIDs
      const taskId = data.task.id || crypto.randomUUID();
      const userId = data.assigneeId;

      // Validate UUID format
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(taskId)) {
        throw new Error(`Invalid task_id UUID format: ${taskId}`);
      }
      if (!uuidRegex.test(userId)) {
        throw new Error(`Invalid user_id UUID format: ${userId}`);
      }

      const { error } = await supabase
        .from('user_notifications')
        .insert([{
          user_id: userId,
          task_id: taskId,
          notification_type: type,
          title,
          message,
          action_url: actionUrl,
          metadata: {
            task_title: data.task.title,
            task_priority: data.task.priority,
            task_due_date: data.task.due_date,
            previous_assignee: data.previousAssignee?.name,
            sender: data.sender?.name
          }
        }]);

      if (error) throw error;

      console.log('In-app notification created successfully');
      
      // Force a manual refresh for Chrome compatibility
      // Dispatch a custom event that components can listen to
      window.dispatchEvent(new CustomEvent('notificationCreated', {
        detail: {
          userId: userId,
          notificationType: type,
          taskId: taskId
        }
      }));
      
    } catch (error) {
      console.error('Error creating in-app notification:', error);
      throw error;
    }
  }

  // Task assignment notification (in-app only)
  static async notifyTaskAssignment(task, assigneeEmail, assigneeName, assigneeId, senderName) {
    return this.sendNotification('task_assigned', {
      task,
      assigneeEmail,
      assigneeName,
      assigneeId,
      senderName,
      sender: { name: senderName }
    });
  }

  // Task reassignment notification (in-app only)
  static async notifyTaskReassignment(task, newAssigneeEmail, newAssigneeName, newAssigneeId, previousAssigneeName, senderName) {
    return this.sendNotification('task_reassigned', {
      task,
      assigneeEmail: newAssigneeEmail,
      assigneeName: newAssigneeName,
      assigneeId: newAssigneeId,
      previousAssigneeName,
      senderName,
      previousAssignee: { name: previousAssigneeName },
      sender: { name: senderName }
    });
  }

  // Overdue task notification (in-app only)
  static async notifyTaskOverdue(task, assigneeEmail, assigneeName, assigneeId) {
    return this.sendNotification('task_overdue', {
      task,
      assigneeEmail,
      assigneeName,
      assigneeId
    });
  }

  // Task update notification (in-app only)
  static async notifyTaskUpdate(task, assigneeId, updateType = 'general') {
    return this.createInAppNotification('task_updated', {
      task,
      assigneeId,
      updateType
    });
  }

  // Batch notify overdue tasks (in-app only)
  static async notifyOverdueTasks(overdueTasks) {
    const notifications = overdueTasks.map(({ task, assigneeEmail, assigneeName, assigneeId }) =>
      this.notifyTaskOverdue(task, assigneeEmail, assigneeName, assigneeId)
    );

    try {
      await Promise.all(notifications);
      console.log(`Successfully sent ${notifications.length} overdue task notifications`);
    } catch (error) {
      console.error('Error sending batch overdue notifications:', error);
    }
  }

  // Legacy method support for backward compatibility
  // eslint-disable-next-line no-unused-vars
  static async sendTaskAssignedNotification(_taskId, _assignedUserId) {
    console.warn('sendTaskAssignedNotification is deprecated. Use notifyTaskAssignment instead.');
    // This would require fetching task and user data first
    // For now, just log the deprecation warning
    return false;
  }

  // eslint-disable-next-line no-unused-vars
  static async sendTaskReassignedNotification(_taskId, _newAssignedUserId) {
    console.warn('sendTaskReassignedNotification is deprecated. Use notifyTaskReassignment instead.');
    return false;
  }

  // eslint-disable-next-line no-unused-vars
  static async sendTaskOverdueNotification(_taskId) {
    console.warn('sendTaskOverdueNotification is deprecated. Use notifyTaskOverdue instead.');
    return false;
  }

  // eslint-disable-next-line no-unused-vars
  static async sendBatchOverdueNotifications(_taskIds) {
    console.warn('sendBatchOverdueNotifications is deprecated. Use notifyOverdueTasks instead.');
    return [];
  }

  // eslint-disable-next-line no-unused-vars
  static async areNotificationsEnabled(_userId, _notificationType = 'email') {
    // For now, assume notifications are always enabled
    // In the future, you could check user preferences from the database
    return true;
  }
}
